import { useContext, useRef, useState, useEffect } from 'react';
import { Context } from '../../../Context';
import { ApartmentViewContext } from '../context/ApartmentViewContext';
import { Splide, SplideSlide } from '@splidejs/react-splide';
import { useTranslation } from 'react-i18next';
import { ReactComponent as DragIcon } from '../../../media/img/icon-drag.svg';
import { ReactComponent as ArrowKeyIcon } from '../assets/icons/icon-arrowkey.svg';
import Loading from '../../Loading';
import styles from '../../../styles/aprt.module.scss';
import AssetLoadError from './AssetLoadError';

function PlanRendererIsometricMode({
	svgContent,
	apartmentData,
	dataUrl,
	compartmentsDictionary,
}) {
	const isometricViewsNumber = 8; //TODO: pass from config.json
	const { t } = useTranslation();

	const splideRef = useRef(null);
	const keyPressTimeoutRef = useRef(null);
	const activeKeyPressRef = useRef(null);
	const [startX, setStartX] = useState(0);
	const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
	const imagesLoadingProgressRef = useRef(0);
	const [imagesLoadingProgress, setImagesLoadingProgress] = useState(0);
	const loadingTimeoutRef = useRef(null);

	const [isLoadError, setIsLoadError] = useState(false);
	const [imageLoadingErrorMessage, setImageLoadingErrorMessage] = useState('');
	const {
		apartmentViewMode,
		setIsometricAngle,
		setApartmentViewMode,
		isIsometryDragging,
		setIsIsometryDragging,
		showIsometryInstructionsPopup,
		setShowIsometryInstructionsPopup,
	} = useContext(ApartmentViewContext);
	const { activeApartmentData, activeApartmentSelectedTier } =
		useContext(Context);
	const isRotatedToNorth = apartmentViewMode.rotatedToNorth;

	const getIsometricAngles = (circleDivisions) => {
		const step = 360 / circleDivisions;
		const offset = circleDivisions === 4 ? step / 2 : 0;
		return Array.from(
			{ length: circleDivisions },
			(_, i) => (i * step + offset) % 360
		);
	};

	const isometricAngles = getIsometricAngles(isometricViewsNumber);

	// const currentTier = apartmentData.compartments[0].ref_tier;
	const currentTier = activeApartmentSelectedTier;

	const getTripleDigitString = (tier) => {
		return tier.toString().padStart(3, '0');
	};
	const getDoubleDigitString = (num) => {
		return num.toString().padStart(2, '0');
	};

	const getShortestAngleDistanceDeg = (angle1, angle2) => {
		const diff = Math.abs(angle1 - angle2) % 360;
		return diff > 180 ? 360 - diff : diff;
	};

	const getClosestAngleIndex = (
		targetAngle,
		anglesArray,
		preferLast = true
	) => {
		let closestIndex = 0;
		let minimumDifference = getShortestAngleDistanceDeg(
			targetAngle,
			anglesArray[0]
		);

		for (let i = 1; i < anglesArray.length; i++) {
			const difference = getShortestAngleDistanceDeg(
				targetAngle,
				anglesArray[i]
			);
			if (
				(!preferLast && difference < minimumDifference) ||
				(preferLast && difference <= minimumDifference)
			) {
				minimumDifference = difference;
				closestIndex = i;
			}
		}
		return closestIndex;
	};

	const viewFromNorthIndex = getClosestAngleIndex(
		apartmentData.azimuth,
		isometricAngles
	);

	const getSplideSlideImageUrl = (index, getHighRes = false) => {
		const uuid = activeApartmentData?.uuid;
		const currentTierStr = getDoubleDigitString(currentTier);
		// const tierIdStr = `${activeApartmentData.building_id}-00-${currentTierStr}`;
		const tierIdStr = activeApartmentData.id_cad
			.split('-')
			.slice(0, 3)
			.join('-');

		return `${dataUrl}apartments/${uuid}/render_360/${uuid}_${tierIdStr}_${(
			index + 1
		)
			.toString()
			.padStart(3, '0')}.png`;
		// const resolution = getHighRes ? "high" : "low";
		// const sizeModifier = getHighRes ? "" : "-800";

		// return `${dataUrl}vis/isometric/${resolution}/${
		// 	apartmentData.uuid
		// }_${getDoubleDigitString(currentTier)}_${apartmentData.id_original}_000${
		// 	index + 1
		// }${sizeModifier}.webp`;
	};

	const handleMouseDown = (e) => {
		setIsIsometryDragging(true);
		setStartX(e.clientX || e.touches[0].clientX);
	};

	useEffect(() => {
		if (!isIsometryDragging) return;

		const handleMouseMove = (e) => {
			if (e.touches) {
				e.preventDefault(); //to prevent scrolling up-down on touch devices when dragging the isometry
			}
			setShowIsometryInstructionsPopup(false);
			setApartmentViewMode(({ rotatedToNorth, ...rest }) => ({
				...rest,
				rotatedToNorth: false,
			}));
			const currentX = e.clientX || e.touches[0]?.clientX;
			const deltaX = currentX - startX;
			const slideWidth =
				document.getElementById('isometricView').offsetWidth /
				isometricViewsNumber;
			const slideShift = Math.round(deltaX / slideWidth);
			let updatedIndex = currentSlideIndex;

			updatedIndex = (currentSlideIndex - slideShift) % isometricViewsNumber;
			if (updatedIndex < 0) updatedIndex += isometricViewsNumber;

			if (splideRef.current && slideShift !== 0) {
				splideRef.current.splide.go(updatedIndex);
				setCurrentSlideIndex(updatedIndex); //TODO: maybe use splideRef.current.splide.index instead of state ?
				setStartX(currentX);
			}
		};

		const handleMouseUp = () => {
			if (!isIsometryDragging) return;
			setIsIsometryDragging(false);
			setStartX(0);
		};

		window.addEventListener('mousemove', handleMouseMove);
		window.addEventListener('mouseup', handleMouseUp);
		window.addEventListener('touchmove', handleMouseMove, { passive: false });
		window.addEventListener('touchend', handleMouseUp);

		return () => {
			window.removeEventListener('mousemove', handleMouseMove);
			window.removeEventListener('mouseup', handleMouseUp);
			window.removeEventListener('touchmove', handleMouseMove, {
				passive: false,
			});
			window.removeEventListener('touchend', handleMouseUp);
		};
	}, [isIsometryDragging, startX, currentSlideIndex, isometricViewsNumber]);

	useEffect(() => {
		const handleKeyDown = (e) => {
			if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
			if (activeKeyPressRef.current === e.key) return;
			clearTimeout(keyPressTimeoutRef.current);
			activeKeyPressRef.current = e.key;

			const performSlideChange = () => {
				setIsIsometryDragging(true);
				setShowIsometryInstructionsPopup(false);

				if (!splideRef.current || activeKeyPressRef.current !== e.key) return;

				const splide = splideRef.current.splide;
				const currentIndex = splide.index;
				let updatedIndex = currentIndex;
				setApartmentViewMode(({ rotatedToNorth, ...rest }) => ({
					...rest,
					rotatedToNorth: false,
				}));

				if (activeKeyPressRef.current === 'ArrowLeft') {
					updatedIndex =
						(currentIndex - 1 + isometricViewsNumber) % isometricViewsNumber;
				} else if (activeKeyPressRef.current === 'ArrowRight') {
					updatedIndex = (currentIndex + 1) % isometricViewsNumber;
				}

				splide.go(updatedIndex);

				// Calculate dynamic delay based on isometricViewsNumber
				const minDelay = 50; // ms (for many views, e.g., 72)
				const maxDelay = 300; // ms (for few views, e.g., 4)
				const minViews = 4;
				const maxViews = 72;
				const range = Math.max(1, maxViews - minViews);
				const clampedViews = Math.max(
					minViews,
					Math.min(isometricViewsNumber, maxViews)
				);
				const delay =
					minDelay +
					(maxDelay - minDelay) * (1 - (clampedViews - minViews) / range);

				// Schedule the next slide change after the calculated delay
				keyPressTimeoutRef.current = setTimeout(performSlideChange, delay);
			};

			performSlideChange();
		};

		const handleKeyUp = (e) => {
			if (e.key === activeKeyPressRef.current) {
				clearTimeout(keyPressTimeoutRef.current);
				activeKeyPressRef.current = null;
				setIsIsometryDragging(false);
			}
		};

		window.addEventListener('keydown', handleKeyDown);
		window.addEventListener('keyup', handleKeyUp);

		return () => {
			window.removeEventListener('keydown', handleKeyDown);
			window.removeEventListener('keyup', handleKeyUp);
			clearTimeout(keyPressTimeoutRef.current);
			activeKeyPressRef.current = null;
		};
	}, []);

	useEffect(() => {
		if (splideRef.current) {
			const splide = splideRef.current.splide;

			const viewRotatedToNorthIndex =
				(viewFromNorthIndex + isometricAngles.length / 2) %
				isometricAngles.length;

			const handleMove = () => {
				const currentIndex = splide.index;
				const indexAdjustedToLocalNorth =
					(currentIndex - viewRotatedToNorthIndex + isometricViewsNumber) %
					isometricViewsNumber;
				const isoAngleAdjustedToLocalNorth =
					isometricAngles[indexAdjustedToLocalNorth];
				setCurrentSlideIndex(currentIndex);
				setIsometricAngle(isoAngleAdjustedToLocalNorth);
			};

			splide.on('move', handleMove);

			return () => {
				splide.off('move', handleMove);
			};
		}
	}, [splideRef.current]);

	//TODO: move to geometryUtils

	const getPathsBoundaryPoints = (pathData) => {
		const commands = pathData.match(/[a-zA-Z][^a-zA-Z]*/g);
		let currentPoint = { x: 0, y: 0 };
		const points = [];

		commands.forEach((command) => {
			const type = command[0];
			const values = command
				.slice(1)
				.trim()
				.split(/[\s,]+/)
				.map(parseFloat);

			switch (type) {
				case 'M':
					currentPoint = { x: values[0], y: values[1] };
					points.push([currentPoint.x, currentPoint.y]);
					break;
				case 'm':
					currentPoint = {
						x: currentPoint.x + values[0],
						y: currentPoint.y + values[1],
					};
					points.push([currentPoint.x, currentPoint.y]);
					break;
				case 'L':
					currentPoint = { x: values[0], y: values[1] };
					points.push([currentPoint.x, currentPoint.y]);
					break;
				case 'l':
					currentPoint = {
						x: currentPoint.x + values[0],
						y: currentPoint.y + values[1],
					};
					points.push([currentPoint.x, currentPoint.y]);
					break;
				case 'H':
					currentPoint.x = values[0];
					points.push([currentPoint.x, currentPoint.y]);
					break;
				case 'h':
					currentPoint.x += values[0];
					points.push([currentPoint.x, currentPoint.y]);
					break;
				case 'V':
					currentPoint.y = values[0];
					points.push([currentPoint.x, currentPoint.y]);
					break;
				case 'v':
					currentPoint.y += values[0];
					points.push([currentPoint.x, currentPoint.y]);
					break;
				case 'Z':
				case 'z':
					// Close path, no new point needed
					break;
				default:
					console.warn(`Unsupported path command: ${type}`);
			}
		});

		return points;
	};

	const getCompartmentDominantVectorAngle = (svgElement, compartment) => {
		const compartmentNumber = compartment.number;

		const compartmentGroup = svgElement.querySelector('g.compartments');
		if (!compartmentGroup) return null;

		const compartmentPath = compartmentGroup.querySelector(
			"path[number='" + compartmentNumber + "']"
		);
		if (!compartmentPath) return null;

		const getCompartmentsBoundaryPoints = (pathElement) => {
			const pathData = pathElement.getAttribute('d');
			if (pathData) return getPathsBoundaryPoints(pathData);
		};

		const compartmentBoundaryPoints =
			getCompartmentsBoundaryPoints(compartmentPath);

		const getBoundaryPointsAdjustedToCenter = (svgElement, boundaryPoints) => {
			const [minX, minY, width, height] = svgElement
				.getAttribute('viewBox')
				.split(' ')
				.map(Number);

			if (!boundaryPoints) return [];
			return boundaryPoints.map((point) => {
				const [x, y] = point;
				return [x - width / 2, y - height / 2];
			});
		};

		const adjustedBoundaryPoints = getBoundaryPointsAdjustedToCenter(
			svgElement,
			compartmentBoundaryPoints
		);

		const getBoundaryDominantVectorAngle = (boundaryPoints) => {
			if (!boundaryPoints || boundaryPoints.length === 0) return null;
			const sum = boundaryPoints.reduce(
				(acc, [x, y]) => {
					acc[0] += x;
					acc[1] += y;
					return acc;
				},
				[0, 0]
			);
			const [sumX, smY] = sum;
			const angleRad = Math.atan2(smY, sumX);
			const angleDeg = (angleRad * 180) / Math.PI;
			return (angleDeg + 360) % 360;
		};

		const dominantAngle = getBoundaryDominantVectorAngle(
			adjustedBoundaryPoints
		);

		return dominantAngle;
	};

	useEffect(() => {
		if (!activeApartmentData || !svgContent) return;

		// Reset loading progress when apartment changes
		imagesLoadingProgressRef.current = 0;
		setImagesLoadingProgress(0);
		clearTimeout(loadingTimeoutRef.current);

		// Set a timeout to force loading completion after 3 seconds
		loadingTimeoutRef.current = setTimeout(() => {
			if (imagesLoadingProgressRef.current < isometricViewsNumber) {
				console.warn(`Forced loading completion. Only ${imagesLoadingProgressRef.current}/${isometricViewsNumber} images loaded.`);
				setImagesLoadingProgress(isometricViewsNumber);
			}
		}, 3000);

		const parser = new DOMParser();
		const svgDoc = parser.parseFromString(svgContent, 'image/svg+xml');
		const svgElement = svgDoc.querySelector('svg');

		// Exit early if SVG parsing failed
		if (!svgElement) return;

		// const indoorCompartmentIds = compartmentsDictionary?.compartments
		const indoorCompartmentIds = compartmentsDictionary
			.filter((room) => room.group !== 'OutdoorSpace')
			.map((room) => room.id);

		const biggestIndoorCompartment = activeApartmentData.compartments
			.filter((compartment) =>
				indoorCompartmentIds.includes(compartment.name_cad)
			)
			.filter((c) => c.ref_tier == activeApartmentSelectedTier)
			.reduce((biggest, current) => {
				return current.area_original > (biggest.area_original || 0)
					? current
					: biggest;
			}, {});

		const balconiesAndTerraces = activeApartmentData.compartments.filter(
			(room) => room.name_cad === 'idBalcony' || room.name_cad === 'idTerrace'
		);

		const getBiggestBalconyOrTerrace = () => {
			if (balconiesAndTerraces.length === 0) return null;

			return balconiesAndTerraces
				.filter((c) => c.ref_tier == activeApartmentSelectedTier)
				.reduce((biggest, current) => {
					return current.area_original > (biggest.area_original || 0)
						? current
						: biggest;
				}, {});
		};
		const biggestBalconyOrTerrace = getBiggestBalconyOrTerrace();

		const getIsometryDominantAngle = () => {
			if (biggestBalconyOrTerrace && Object.keys(biggestBalconyOrTerrace).length > 0) {
				const angle = getCompartmentDominantVectorAngle(
					svgElement,
					biggestBalconyOrTerrace
				);
				return angle !== null ? angle : activeApartmentData.azimuth;
			} else if (biggestIndoorCompartment && Object.keys(biggestIndoorCompartment).length > 0) {
				const angle = getCompartmentDominantVectorAngle(
					svgElement,
					biggestIndoorCompartment
				);
				return angle !== null ? angle : activeApartmentData.azimuth;
			} else {
				return activeApartmentData.azimuth;
			}
		};

		const isometryDominantAngle = getIsometryDominantAngle();
		const bestAngleIndex = getClosestAngleIndex(
			//TODO: refactor this to find closest angle to view form an angle (45, 135, 225, 315). Dead on the angle? with some tolerance, like +/- 14deg (2 images each side for a 72image isometry)
			isometryDominantAngle,
			isometricAngles
		);

		const viewRotatedToNorthIndex =
			(viewFromNorthIndex + isometricAngles.length / 2) %
			isometricAngles.length;

		//TODO: REFACTOR THIS TEMP SOLUTION TO AVOID ENFACE VIEW ON LOAD
		if (isIsometryDragging) return;
		if (!splideRef.current) return;

		if (isRotatedToNorth) {
			splideRef.current.splide.go(viewRotatedToNorthIndex);
		} else if (bestAngleIndex % 2 === 0) {
			splideRef.current.splide.go(bestAngleIndex + 1);
		} else {
			splideRef.current.splide.go(bestAngleIndex);
		}

		return () => {
			clearTimeout(loadingTimeoutRef.current);
		};
	}, [apartmentData, svgContent, isRotatedToNorth]);

	const isLoading = imagesLoadingProgress != isometricViewsNumber;

	return (
		<>
			{apartmentData && (
				<div
					className={`${styles.overlaySVG}`}
				>
					{imagesLoadingProgress != isometricViewsNumber && (
						<div className={styles.loading_overlay}>
							<Loading
								progress={`${
									(imagesLoadingProgress / isometricViewsNumber) * 100
								}%`}
							/>
						</div>
					)}
					{isLoadError && (
						<AssetLoadError
							dataUrl={dataUrl}
							loadingErrorMessage={imageLoadingErrorMessage}
						/>
					)}
					<div
						className={styles.isometric_container}
						id="isometricView"
						onMouseDown={handleMouseDown}
						onTouchStart={handleMouseDown}
					>
						{(showIsometryInstructionsPopup && !isLoading && !isLoadError)&&(
							<div
								className={styles.instruction_container}
								onClick={() => setShowIsometryInstructionsPopup(false)}
							>
								<div className={styles.instruction_box}>
									<div className={styles.instruction_keyIcon}>
										<ArrowKeyIcon
											className={styles.instruction_icon}
											style={{ transform: 'rotate(180deg)' }}
										/>
									</div>
									<div className={styles.instruction_keyIcon}>
										<ArrowKeyIcon className={styles.instruction_icon} />
									</div>
									<span style={{ display: 'inline-block' }}>
										{t('use_arrow_keys_or')}{' '}
									</span>
								</div>
								<div className={styles.instruction_box}>
									<DragIcon class={styles.instruction_icon} />
									<span style={{ display: 'inline-block' }}>
										{t('drag_to_rotate')}{' '}
									</span>
								</div>
							</div>
						)}
						{(!isLoadError)&&(
						<Splide
							ref={splideRef}
							options={{
								type: 'fade',
								rewind: true,
								pagination: false,
								arrows: false,
								fixedWidth: '100%',
								fixedHeight: '100%',
								drag: false,
							}}
							className={styles.isometric_splide}
						>
							{Array.from({ length: isometricViewsNumber }, (_, index) => (
								<SplideSlide
									key={index}
									className={styles.isometric_splide_slide}
								>
									<img
										className={`${styles.isometric_slide_image} ${
											isIsometryDragging ? styles.dragging : ''
										}`}
										src={getSplideSlideImageUrl(index, false)}
										onLoad={() => {
											if (
												imagesLoadingProgressRef.current < isometricViewsNumber
											) {
												imagesLoadingProgressRef.current++;
												setImagesLoadingProgress(
													imagesLoadingProgressRef.current
												);
											}
										}}
										onError={() => {
											// Count failed images as loaded to prevent UI freeze
											if (
												imagesLoadingProgressRef.current < isometricViewsNumber
											) {
												imagesLoadingProgressRef.current++;
												setImagesLoadingProgress(
													imagesLoadingProgressRef.current
												);
											}
											setImageLoadingErrorMessage(`${t("img_load_error_1")} ${t("load_error_1")}`);
											setIsLoadError(true);
										}}
									/>
								</SplideSlide>
							))}
						</Splide>
						)}
					</div>
				</div>
			)}
		</>
	);
}

export default PlanRendererIsometricMode;